/**
 * De supabase-kant van de beeldreeks: uploaden, opslaan en opruimen.
 * De pure logica staat in socialImages.ts.
 */
import { supabase } from './supabase';
import { compressImage } from './imageCompression';
import { deleteFromStorage, uploadToStorage } from './storageUpload';
import { coverUrl, type SocialImage } from './socialImages';

/** Comprimeert en uploadt één bestand naar het social-pad van deze post. */
export async function uploadSocialImage(
  file: File,
  userId: string,
  postId: string,
): Promise<SocialImage> {
  const compressed = await compressImage(file);
  const result = await uploadToStorage(compressed.blob, userId, 'social', { postId });
  return { url: result.publicUrl, storage_path: result.storagePath };
}

/**
 * Schrijft de reeks weg en houdt de invariant in stand: image_url is altijd
 * het eerste beeld. Wordt na elke wijziging aangeroepen, zodat een klant die
 * het scherm verlaat zonder in te plannen zijn uploads niet kwijt is.
 */
export async function saveSocialImages(
  postId: string,
  images: SocialImage[],
  fallbackImageUrl: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('social_media_posts')
    .update({
      images: images.length > 0 ? images : null,
      image_url: coverUrl(images, fallbackImageUrl),
    })
    .eq('id', postId);
  if (error) throw error;
}

/**
 * True als een ANDERE post nog naar dit bestand wijst. Nodig omdat een reeks
 * overgenomen wordt zonder opnieuw te uploaden: twee posts delen dan bestanden.
 */
export async function isStoragePathInUse(
  storagePath: string,
  exceptPostId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('social_media_posts')
    .select('id')
    .neq('id', exceptPostId)
    // NIET .contains() gebruiken: postgrest-js ziet een JS-array als een
    // NATIEVE Postgres-array en doet er join(',') op, wat voor objecten
    // "cs.{[object Object]}" oplevert. Voor een jsonb-kolom moet de waarde als
    // JSON-string mee. Empirisch vastgesteld op 2026-09-08.
    .filter('images', 'cs', JSON.stringify([{ storage_path: storagePath }]))
    .limit(1);
  if (error) {
    // Bij twijfel niets weggooien: een weesbestand is minder erg dan een
    // gebroken beeld in een andere post.
    console.warn('Kon hergebruik niet controleren:', error.message);
    return true;
  }
  return (data ?? []).length > 0;
}

/**
 * Verwijdert het bestand alleen als geen andere post het nog gebruikt.
 * Een beeld zonder storage_path komt niet uit deze laag maar is de uitgelichte
 * afbeelding van de BLOG; die mag je hier nooit weggooien.
 */
export async function removeSocialImageFile(
  image: SocialImage,
  postId: string,
  userId: string,
): Promise<void> {
  if (!image.storage_path) return;
  if (await isStoragePathInUse(image.storage_path, postId)) return;
  try {
    await deleteFromStorage(image.storage_path, userId);
  } catch (err) {
    // Opruimen mag de gebruiker nooit blokkeren.
    console.warn('Kon beeld niet verwijderen uit storage:', err);
  }
}
